import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { 
  Phone, Mail, MapPin, CheckCircle, ArrowRight, 
  Home, PoundSterling, Clock, Shield, Star, Users
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const LandlordValuation = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    postcode: '',
    property_type: 'flat',
    bedrooms: '2'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/leads/landlord`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          property_type: formData.property_type,
          postcode: formData.postcode,
          bedrooms: parseInt(formData.bedrooms),
          message: `Free Rental Valuation Request - ${formData.bedrooms} bed ${formData.property_type}`,
          source: 'valuation_landing_page'
        })
      });

      if (response.ok) {
        setSubmitted(true);
        toast({ title: "Valuation Request Received!", description: "We'll call you within 24 hours." });
      } else {
        toast({ title: "Error", description: "Please try again or call us directly.", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Please try again or call us directly.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#404040] to-[#2a2a2a] flex items-center justify-center p-4">
        <Card className="max-w-lg w-full text-center">
          <CardContent className="p-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-[#404040] mb-4">Thank You, {formData.name}!</h1>
            <p className="text-gray-600 mb-6">
              We've received your valuation request for your property in <strong>{formData.postcode}</strong>.
            </p>
            <div className="bg-[#FF8C42]/10 p-4 rounded-lg mb-6">
              <p className="text-[#404040] font-medium">What happens next?</p>
              <p className="text-sm text-gray-600 mt-2">
                One of our local lettings experts will call you within 24 hours to discuss your property and provide a free rental estimate.
              </p>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Can't wait? Call us now:
            </p>
            <a href="tel:02081898889" className="inline-flex items-center gap-2 text-[#FF8C42] font-bold text-xl hover:underline">
              <Phone className="w-5 h-5" /> 0208 189 8889
            </a>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#404040] to-[#2a2a2a]">
      {/* Hero Section */}
      <div className="container mx-auto px-4 py-12 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          
          {/* Left - Value Proposition */}
          <div className="text-white">
            <div className="inline-block bg-[#FF8C42] text-white text-sm font-semibold px-4 py-1 rounded-full mb-6">
              FREE FOR HARROW LANDLORDS
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              How Much Rent Could Your Property Achieve?
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Get a free, no-obligation rental valuation from Harrow's trusted letting experts. We'll show you exactly what your property could earn.
            </p>
            
            {/* Trust Signals */}
            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-[#FF8C42]" />
                <span className="text-gray-200">Managing dozens of properties across Harrow & North West London</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-[#FF8C42]" />
                <span className="text-gray-200">Strong tenant demand means minimal void periods</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-[#FF8C42]" />
                <span className="text-gray-200">Most instructions from referrals & repeat landlords</span>
              </div>
            </div>

            {/* Testimonial */}
            <div className="bg-white/10 backdrop-blur p-6 rounded-xl border border-white/20">
              <div className="flex gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-[#FF8C42] text-[#FF8C42]" />
                ))}
              </div>
              <p className="text-gray-200 italic mb-3">
                "Harry & Jacobs took all the stress out of managing my rental. Communication is excellent and rent is always on time."
              </p>
              <p className="text-[#FF8C42] font-medium">— Landlord, Harrow</p>
            </div>
          </div>

          {/* Right - Form */}
          <div>
            <Card className="shadow-2xl border-0">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-[#404040]">Get Your Free Valuation</h2>
                  <p className="text-gray-500 mt-2">Takes 30 seconds • No obligation</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Your Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="John Smith"
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="07XXX XXXXXX"
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="you@example.com"
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="postcode">Property Postcode *</Label>
                    <Input
                      id="postcode"
                      value={formData.postcode}
                      onChange={(e) => setFormData({...formData, postcode: e.target.value.toUpperCase()})}
                      placeholder="HA1 1AA"
                      required
                      className="mt-1"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="property_type">Property Type</Label>
                      <select
                        id="property_type"
                        value={formData.property_type}
                        onChange={(e) => setFormData({...formData, property_type: e.target.value})}
                        className="w-full mt-1 p-2 border rounded-md"
                      >
                        <option value="flat">Flat</option>
                        <option value="house">House</option>
                        <option value="maisonette">Maisonette</option>
                        <option value="studio">Studio</option>
                        <option value="room">Room (HMO)</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="bedrooms">Bedrooms</Label>
                      <select
                        id="bedrooms"
                        value={formData.bedrooms}
                        onChange={(e) => setFormData({...formData, bedrooms: e.target.value})}
                        className="w-full mt-1 p-2 border rounded-md"
                      >
                        <option value="0">Studio</option>
                        <option value="1">1 Bed</option>
                        <option value="2">2 Beds</option>
                        <option value="3">3 Beds</option>
                        <option value="4">4 Beds</option>
                        <option value="5">5+ Beds</option>
                      </select>
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-[#FF8C42] hover:bg-[#FF6B35] text-white py-6 text-lg font-semibold"
                    disabled={loading}
                  >
                    {loading ? 'Submitting...' : 'Get My Free Valuation'}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>

                  <p className="text-center text-xs text-gray-400">
                    By submitting, you agree to be contacted about your valuation request.
                  </p>
                </form>

                {/* Call Option */}
                <div className="mt-6 pt-6 border-t text-center">
                  <p className="text-gray-500 text-sm mb-2">Prefer to talk?</p>
                  <a href="tel:02081898889" className="inline-flex items-center gap-2 text-[#FF8C42] font-bold text-lg hover:underline">
                    <Phone className="w-5 h-5" /> 0208 189 8889
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Why Choose Us Strip */}
      <div className="bg-white py-12">
        <div className="container mx-auto px-4">
          <h3 className="text-2xl font-bold text-center text-[#404040] mb-8">Why Harrow Landlords Choose Us</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-14 h-14 bg-[#FF8C42]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <PoundSterling className="w-7 h-7 text-[#FF8C42]" />
              </div>
              <h4 className="font-semibold text-[#404040]">Maximise Your Rent</h4>
              <p className="text-sm text-gray-500 mt-1">Expert pricing to achieve the best returns</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 bg-[#FF8C42]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <Clock className="w-7 h-7 text-[#FF8C42]" />
              </div>
              <h4 className="font-semibold text-[#404040]">Fast Tenant Finding</h4>
              <p className="text-sm text-gray-500 mt-1">Strong local demand minimises voids</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 bg-[#FF8C42]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <Shield className="w-7 h-7 text-[#FF8C42]" />
              </div>
              <h4 className="font-semibold text-[#404040]">Full Compliance</h4>
              <p className="text-sm text-gray-500 mt-1">We handle all legal requirements</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 bg-[#FF8C42]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <Users className="w-7 h-7 text-[#FF8C42]" />
              </div>
              <h4 className="font-semibold text-[#404040]">Local Experts</h4>
              <p className="text-sm text-gray-500 mt-1">Harrow specialists who know the market</p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Footer */}
      <div className="bg-[#FF8C42] py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-white text-lg mb-4">
            Ready to find out what your property could earn?
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="#top" 
              onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              className="inline-flex items-center justify-center gap-2 bg-white text-[#FF8C42] px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Get Free Valuation
            </a>
            <a 
              href="tel:02081898889"
              className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition-colors"
            >
              <Phone className="w-5 h-5" /> 0208 189 8889
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandlordValuation;
